/**
 * What a capability card shows, from the skill's readiness checks
 * (packages/domain/agent-skills/domain/services/readinessRules.js):
 *
 * - "ready": can be turned on now; the footer names the system it uses.
 * - "waiting": can be turned on now and goes live by itself once the WhatsApp
 *   is connected (or its message template approved). The firm does not come
 *   back to it: connecting the WhatsApp once releases every waiting item.
 * - "blocked": needs a system the firm has not connected; the card links to it.
 * - "plan": not in the firm's plan.
 * - "unavailable": something else is missing that the firm cannot fix here
 *   (e.g. no automatic setup for its legal system yet).
 *
 * Automations (reminders, birthdays) are set up in a dialog, after the
 * WhatsApp: "later" until it is connected, then "automation" (open the
 * dialog), "pending" while Meta reviews the template it created, and
 * "in_use". They are not switched on here, since nothing would turn them on
 * by itself when the WhatsApp connects.
 */

const WAITS_FOR_WHATSAPP = new Set(["channel.whatsapp", "channel.health", "template.approved"]);
const SYSTEM_OF = [
  ["legal.", "Precisa do sistema jurídico"],
  ["zapsign.", "Precisa da ZapSign"],
  ["asaas.", "Precisa do Asaas"],
];

const isMissing = (c) => c.status === "missing" || c.status === "unavailable";

/**
 * @param {{kind: string, required?: boolean, availability?: {checks?: object[]}}} skill
 * @param {{legalProviderLabel?: string|null}} [ctx]
 * @returns {{state: 'ready'|'waiting'|'blocked'|'plan'|'unavailable'|'later'|'automation'|'pending'|'in_use', footer: string}}
 */
export function capabilityState(skill, { legalProviderLabel = null } = {}) {
  const checks = skill.availability?.checks || [];
  const missing = checks.filter(isMissing);

  if (missing.some((c) => String(c.key).startsWith("plan."))) {
    return { state: "plan", footer: "Fora do seu plano" };
  }
  for (const [prefix, footer] of SYSTEM_OF) {
    if (missing.some((c) => String(c.key).startsWith(prefix))) return { state: "blocked", footer };
  }
  if (skill.kind !== "agent") {
    if (skill.awaitingTemplate === "pending") return { state: "pending", footer: "Aguardando aprovação da Meta" };
    if (skill.awaitingTemplate === "rejected") return { state: "automation", footer: "A Meta recusou o modelo" };
    if (missing.some((c) => WAITS_FOR_WHATSAPP.has(c.key))) return { state: "later", footer: "Configura depois do WhatsApp" };
    return skill.inUse ? { state: "in_use", footer: "Em uso" } : { state: "automation", footer: "Pronto para configurar" };
  }
  // Anything else missing (e.g. no automatic setup for this legal system) blocks
  // too, as the server does; only waiting for the WhatsApp lets the card on.
  const other = missing.find((c) => !WAITS_FOR_WHATSAPP.has(c.key));
  if (other) {
    return { state: "unavailable", footer: other.key === "skill.provider" ? "Com o seu sistema, só pelo Touch Builder" : other.title || "Indisponível por enquanto" };
  }
  if (missing.some((c) => WAITS_FOR_WHATSAPP.has(c.key))) {
    const noChannel = missing.some((c) => c.key === "channel.whatsapp");
    return { state: "waiting", footer: noChannel ? "Liga com o WhatsApp" : "Liga quando a Meta aprovar" };
  }

  if (skill.required) return { state: "ready", footer: "Base do agente" };
  if (checks.some((c) => String(c.key).startsWith("legal."))) return { state: "ready", footer: legalProviderLabel || "Sistema jurídico" };
  if (checks.some((c) => String(c.key).startsWith("zapsign."))) return { state: "ready", footer: "ZapSign" };
  return { state: "ready", footer: "WhatsApp" };
}

/** Whether the firm can turn the card on in this step (agent skills only). */
export function canTurnOn(state) {
  return state === "ready" || state === "waiting";
}

/** Screen that sets up an automation skill, for the legal system in use. */
export function automationSetupPath(accountPath, accountId, workflow, provider) {
  if (!provider) return null;
  if (workflow === "reminder") {
    return provider === "faz"
      ? accountPath(accountId, "structured-automations/faz-task/new")
      : accountPath(accountId, `structured-automations/task/new?provider=${provider}`);
  }
  if (workflow === "birthday") return accountPath(accountId, `structured-automations/birthday/new?provider=${provider}`);
  return null;
}
